import { List, Card, Button, Typography, Tag, Spin, Alert, App, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useMe, useSwitchTenant } from '../../api/auth';
import { useTenants } from '../../api/tenants';
import { useSessionStore } from '../../stores/sessionStore';

const ROLE_LABEL: Record<string, string> = {
  admin: '管理员',
  editor: '编辑',
  viewer: '审核员',
};

export function SelectTenantPage() {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { user, setTenant, enterTenantAsPlatform, clearTenant } = useSessionStore();

  // 平台管理员「直接进入（平台管理）」：清掉可能残留的旧产品，再进主框架
  const onEnterPlatform = () => {
    clearTenant();
    navigate('/', { replace: true });
  };
  const isPlatformAdmin = user?.is_platform_admin ?? false;
  const meQuery = useMe(!isPlatformAdmin); // 平台管理员不靠成员资格选产品
  const allTenants = useTenants(isPlatformAdmin); // 平台管理员列「所有产品」

  const switchMut = useSwitchTenant();

  // 一般成员：走 switch-tenant 拿 tenant_jwt
  const onPickMember = (tenantId: string) => {
    switchMut.mutate(tenantId, {
      onSuccess: (res) => {
        setTenant(res.tenant_jwt, res.expires_at, res.tenant, res.role);
        navigate('/', { replace: true });
      },
      onError: () => message.error('切换产品失败'),
    });
  };

  // 平台管理员：本地进入（用 platform token 跨产品，无需 switch-tenant）
  const onEnterAsPlatform = (t: { tenant_id: string; name: string; code: string }) => {
    enterTenantAsPlatform({ id: t.tenant_id, name: t.name, code: t.code });
    navigate('/', { replace: true });
  };

  return (
    <div
      style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#f3f3f3' }}
    >
      <Card style={{ width: 560 }}>
        <Typography.Title level={4}>选择产品</Typography.Title>

        {isPlatformAdmin ? (
          <>
            <Alert
              style={{ marginBottom: 16 }}
              type="info"
              showIcon
              message="您是平台管理员"
              description={
                <Space>
                  可进入任意产品查看/管理业务数据；或直接进入只做平台管理。
                  <Button size="small" type="link" onClick={onEnterPlatform}>
                    直接进入（平台管理）→
                  </Button>
                </Space>
              }
            />
            {allTenants.isLoading ? (
              <Spin />
            ) : allTenants.isError ? (
              <Alert type="error" message="无法载入产品列表" showIcon />
            ) : (
              <List
                header={<Typography.Text strong>所有产品</Typography.Text>}
                dataSource={allTenants.data ?? []}
                locale={{ emptyText: '尚无产品，请先到「产品管理」创建' }}
                renderItem={(t) => (
                  <List.Item
                    actions={[
                      <Button key="enter" type="primary" onClick={() => onEnterAsPlatform(t)}>
                        进入
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={t.name}
                      description={
                        <Space>
                          <Tag>{t.code}</Tag>
                          <Tag color={t.status === 'active' ? 'green' : 'default'}>
                            {t.status === 'active' ? '启用' : '归档'}
                          </Tag>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </>
        ) : meQuery.isLoading ? (
          <Spin />
        ) : meQuery.isError ? (
          <Alert type="error" message="无法载入产品列表" showIcon />
        ) : (
          <List
            dataSource={(meQuery.data?.tenants ?? []).filter((t) => t.status === 'active')}
            locale={{ emptyText: '您尚未加入任何产品，请联系管理员' }}
            renderItem={(t) => (
              <List.Item
                actions={[
                  <Button
                    key="enter"
                    type="primary"
                    loading={switchMut.isPending && switchMut.variables === t.tenant_id}
                    onClick={() => onPickMember(t.tenant_id)}
                  >
                    进入
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  title={t.name || t.tenant_id}
                  description={
                    <Space>
                      {t.code && <Tag>{t.code}</Tag>}
                      <Tag color="blue">{ROLE_LABEL[t.role] ?? t.role}</Tag>
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Card>
    </div>
  );
}
